// timeSlotUtils.js
//
// Matches the exact shape used in HomeDashboard's slotsAvailable:
//   [
//     { day: "SUN", timeSlots: [{ from: "10:00", to: "11:00" }, ...] },
//     { day: "MON", timeSlots: [{ from: "22:00", to: "23:00" }] },
//     ...
//   ]
//
// mergeOfferedHours() flattens + merges back-to-back ranges per day into
// one compact pill instead of one pill per raw slot:
//   "Sun 10:00–11:00", "Sun 11:00–12:00", "Sun 12:00–13:00" (3 pills)
//     -> "Sun 10a–1p" (1 pill)

const DAY_ORDER = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

function toDayAbbr(code) {
  return code.charAt(0) + code.slice(1).toLowerCase(); // "SUN" -> "Sun"
}

/** "10:00" -> 10, "22:30" -> 22.5 */
function toHourNumber(hhmm) {
  const [h, m] = String(hhmm).split(":").map(Number);
  return h + (m ? m / 60 : 0);
}

/** 10 -> "10a", 22 -> "10p", 13 -> "1p", 0 -> "12a", 10.5 -> "10:30a" */
function formatHourCompact(hourNum) {
  const h24 = Math.floor(hourNum);
  const suffix = h24 >= 12 ? "p" : "a";
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  const hasHalf = hourNum % 1 !== 0;
  return hasHalf ? `${h12}:30${suffix}` : `${h12}${suffix}`;
}

/**
 * @param {{day: string, timeSlots: {from: string, to: string}[]}[]} slotsAvailable
 * @returns {{key: string, label: string}[]} one entry per merged range, in
 *   Sun→Sat order, e.g. [{ key: "SUN-0", label: "Sun 10a–3p" }, ...]
 */
export function mergeOfferedHours(slotsAvailable = []) {
  const byDay = new Map();

  for (const slot of slotsAvailable) {
    const ranges = (slot.timeSlots || [])
      .filter((ts) => ts?.from && ts?.to)
      .map((ts) => ({ start: toHourNumber(ts.from), end: toHourNumber(ts.to) }));
    if (ranges.length) {
      byDay.set(slot.day, [...(byDay.get(slot.day) || []), ...ranges]);
    }
  }

  const result = [];

  for (const dayCode of DAY_ORDER) {
    const ranges = byDay.get(dayCode);
    if (!ranges || ranges.length === 0) continue;

    ranges.sort((a, b) => a.start - b.start);

    let current = { ...ranges[0] };
    let idx = 0;
    const pushCurrent = () => {
      result.push({
        key: `${dayCode}-${idx++}`,
        label: `${toDayAbbr(dayCode)} ${formatHourCompact(current.start)}–${formatHourCompact(current.end)}`,
      });
    };

    for (let i = 1; i < ranges.length; i++) {
      const next = ranges[i];
      if (next.start <= current.end) {
        current.end = Math.max(current.end, next.end); // contiguous/overlapping -> merge
      } else {
        pushCurrent();
        current = { ...next };
      }
    }
    pushCurrent();
  }

  return result;
}